import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { Alert, Box, Paper, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";

const OrgDataExportPage = () => {
  const { userData, organizationId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");

  if (userData?.role !== "admin") {
    return <Navigate to="/dashboard/settings" replace />;
  }

  const handleExport = async () => {
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    setDownloadUrl("");

    try {
      const callable = firebase
        .app()
        .functions("us-central1")
        .httpsCallable("exportOrgPromptContent");

      const result = await callable({ organizationId });
      setDownloadUrl(result.data?.downloadUrl || "");
      setSuccessMessage(
        `Export ready (${result.data?.promptCount ?? 0} prompts). Link expires in 24 hours.`,
      );
    } catch (err) {
      setErrorMessage(err.message || "Export failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <DashboardPageHeader
        title="Export organization data"
        subtitle="Download your organization's institution-wide prompt settings, change history, and embedded document metadata in a portable JSON format."
      />

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage("")} sx={{ mb: 2 }}>
          {successMessage}
          {downloadUrl && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <a href={downloadUrl} target="_blank" rel="noreferrer">
                Download export file
              </a>
            </Typography>
          )}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" onClose={() => setErrorMessage("")} sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This export includes all organization-scoped prompt customizations and their audit
          history. Clinical session content is not included.
        </Typography>
        <LoadingButton variant="contained" loading={loading} onClick={handleExport}>
          Export P3 content
        </LoadingButton>
      </Paper>
    </Box>
  );
};

export default OrgDataExportPage;
